// src/components/Supplier/SupplierDashboard.jsx
import React, { useState, useEffect, useRef } from 'react';
import { 
  Menu, 
  LayoutDashboard, 
  Users, 
  Tag, 
  LogOut, 
  ChevronDown, 
  Building2, 
  ExternalLink,
  Edit3,
  MousePointerClick,
  TrendingUp,
  Mail,
  Send,
  Eye,
  Check,
  Search,
  Sparkles
} from 'lucide-react';
import SupplierProfileModal from './SupplierProfileModal';
import SupplierOffersManager from './SupplierOffersManager';
import './SupplierDashboard.css';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'https://api.lbnhyderabad.com';

export default function SupplierDashboard({ currentSupplier, onLogout, onUpdateSupplier }) {
  const [activeTab, setActiveTab] = useState('dashboard');
  const [stats, setStats] = useState(null);
  const [members, setMembers] = useState([]);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [mobileDrawerOpen, setMobileDrawerOpen] = useState(false);
  const [profileDropdownOpen, setProfileDropdownOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [memberSearch, setMemberSearch] = useState('');
  const [sendingId, setSendingId] = useState(null);
  const [sentNotice, setSentNotice] = useState('');

  const dropdownRef = useRef(null);

  // Close profile dropdown when clicking outside
  useEffect(() => {
    function handleClickOutside(e) {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setProfileDropdownOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Fetch initial analytical and member data
  useEffect(() => {
    if (currentSupplier?.id) {
      fetch(`${API_BASE_URL}/api/supplier/${currentSupplier.id}/stats`)
        .then(res => res.json())
        .then(json => {
          if (json.success) {
            setStats(json.data);
            setMembers(json.data.members || []);
          }
        })
        .catch(err => console.error('Error loading stats:', err));
    }
  }, [currentSupplier, activeTab]);

  const handleHamburgerClick = () => {
    if (window.innerWidth <= 868) {
      setMobileDrawerOpen(!mobileDrawerOpen);
    } else {
      setSidebarCollapsed(!sidebarCollapsed);
    }
  };

  // One-click send promotional offer email to member
  const handleSendOfferEmail = async (member) => {
    setSendingId(member.id);
    try {
      const res = await fetch(`${API_BASE_URL}/api/supplier/${currentSupplier.id}/send-offer`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          memberId: member.id,
          memberEmail: member.email
        })
      });
      const data = await res.json();
      if (data.success) {
        setSentNotice(`Offer dispatched to ${member.full_name}`);
        setTimeout(() => setSentNotice(''), 3500);
      }
    } catch (e) {
      console.error(e);
    } finally {
      setSendingId(null);
    }
  };

  const filteredMembers = members.filter(m =>
    m.full_name?.toLowerCase().includes(memberSearch.toLowerCase()) ||
    m.membership_number?.toLowerCase().includes(memberSearch.toLowerCase()) ||
    m.district?.toLowerCase().includes(memberSearch.toLowerCase()) ||
    m.privilege_code?.toLowerCase().includes(memberSearch.toLowerCase())
  );

  const totalMembers = stats?.totalMembers || 1;
  const totalOffers = stats?.totalOffers || 0;
  const totalInteractions = stats?.interactions?.total_interactions || 0;
  const promoCopies = stats?.interactions?.promo_copies || 0;
  const websiteClicks = stats?.interactions?.website_clicks || 0;
  const uniqueVisitors = stats?.interactions?.unique_visitors || 0;
  const conversionRate = Math.min(100, Math.round((uniqueVisitors / totalMembers) * 100));

  return (
    <div className="sup-dashboard-root">
      {/* Edit Profile Modal */}
      {isEditModalOpen && (
        <SupplierProfileModal
          supplier={currentSupplier}
          onClose={() => setIsEditModalOpen(false)}
          onUpdateSuccess={(updated) => onUpdateSupplier(updated)}
        />
      )}

      {/* Mobile Drawer Backdrop */}
      {mobileDrawerOpen && (
        <div
          style={{ position: 'fixed', inset: 0, background: 'rgba(2, 44, 34, 0.5)', zIndex: 998 }}
          onClick={() => setMobileDrawerOpen(false)}
        />
      )}

      {/* Sidebar */}
      <aside className={`sup-sidebar ${sidebarCollapsed ? 'collapsed' : ''} ${mobileDrawerOpen ? 'mobile-open' : ''}`}>
        <div className="sup-sidebar-header">
          <div className="sup-crest">
            <Building2 size={20} />
          </div>
          <div>
            <span className="sup-brand-title">Privilege</span>
            <span style={{ fontSize: '0.6rem', color: '#f59e0b', fontWeight: 800, display: 'block' }}>
              SUPPLIER CONSOLE
            </span>
          </div>
        </div>

        <nav className="sup-nav-list">
          <button
            className={`sup-nav-item ${activeTab === 'dashboard' ? 'active' : ''}`}
            onClick={() => { setActiveTab('dashboard'); setMobileDrawerOpen(false); }}
          >
            <LayoutDashboard size={18} />
            <span>Dashboard & Metrics</span>
          </button>

          <button
            className={`sup-nav-item ${activeTab === 'members' ? 'active' : ''}`}
            onClick={() => { setActiveTab('members'); setMobileDrawerOpen(false); }}
          >
            <Users size={18} />
            <span>Accredited Members</span>
          </button>

          <button
            className={`sup-nav-item ${activeTab === 'offers' ? 'active' : ''}`}
            onClick={() => { setActiveTab('offers'); setMobileDrawerOpen(false); }}
          >
            <Tag size={18} />
            <span>My Commercial Offers</span>
          </button>
        </nav>

        <div className="sup-sidebar-footer">
          <button onClick={onLogout} className="btn-sup-logout">
            <LogOut size={16} />
            <span>Logout</span>
          </button>
        </div>
      </aside>

      {/* Main Content Stage */}
      <div className={`sup-stage ${sidebarCollapsed ? 'expanded' : ''}`}>
        {/* Top Header */}
  <header className="sup-header">
  {/* Left: Menu trigger & Titles */}
  <div className="sup-header-left">
    <button
      className="sup-hamburger-btn"
      onClick={handleHamburgerClick}
      aria-label="Toggle navigation"
    >
      <Menu size={18} color="#064e3b" />
    </button>
    
    <div className="sup-header-headings">
      <h2 className="sup-header-title">
        {activeTab === 'dashboard' && 'Enterprise Performance'}
        {activeTab === 'members' && 'Accredited Members'}
        {activeTab === 'offers' && 'Corporate Deals'}
      </h2>
      <span className="sup-header-sub">
        <strong>{currentSupplier?.company_name}</strong>
        <span className="sup-header-divider">•</span>
        <span className="sup-header-category">{currentSupplier?.core_offering_category}</span>
      </span>
    </div>
  </div>

  {/* Right: Profile Trigger & Dropdown */}
  <div className="sup-profile-wrapper" ref={dropdownRef}>
    <button 
      className="sup-profile-trigger" 
      onClick={() => setProfileDropdownOpen(!profileDropdownOpen)}
    >
      <div className="sup-avatar-badge">
        {currentSupplier?.company_name ? currentSupplier.company_name.charAt(0).toUpperCase() : 'S'}
      </div>
      <div className="sup-profile-text-group">
        <span className="sup-profile-role">ACCREDITED VENDOR</span>
        <span className="sup-profile-name">{currentSupplier?.contact_person}</span>
      </div>
      <ChevronDown size={14} color="#064e3b" className="sup-profile-chevron" />
    </button>

    {profileDropdownOpen && (
      <div className="sup-profile-popover">
        <div className="sup-popover-email-box">
          <div className="sup-popover-label">Registered Email</div>
          <strong className="sup-popover-email">{currentSupplier?.email}</strong>
        </div>

        <button
          className="sup-popover-item"
          onClick={() => { setIsEditModalOpen(true); setProfileDropdownOpen(false); }}
        >
          <Edit3 size={15} color="#059669" />
          <span>Edit Profile & Catalog</span>
        </button>

        <button
          className="sup-popover-item danger"
          onClick={onLogout}
        >
          <LogOut size={15} />
          <span>Sign Out</span>
        </button>
      </div>
    )}
  </div>
</header>

        {/* Feedback Alert Toast */}
        {sentNotice && (
          <div style={{
            position: 'fixed', top: '74px', right: '24px', zIndex: 1100,
            background: '#064e3b', color: '#ffffff', border: '1px solid #10b981',
            padding: '10px 18px', borderRadius: '10px', fontSize: '0.82rem', fontWeight: 700,
            boxShadow: '0 8px 24px rgba(0,0,0,0.2)', display: 'flex', alignItems: 'center', gap: '8px'
          }}>
            <Sparkles size={16} color="#f59e0b" />
            <span>{sentNotice}</span>
          </div>
        )}

        <div className="sup-content-pane">
          {/* ================= TAB 1: DASHBOARD & METRICS ================= */}
          {activeTab === 'dashboard' && (
            <>
              {/* 4 Clean Metric Cards */}
              <div className="sup-kpi-master-grid">
                {/* 1. Accredited Members Reach */}
                <div className="sup-kpi-card">
                  <div className="sup-card-top-stripe" />
                  <div className="sup-kpi-head">
                    <span className="sup-kpi-title">Accredited Members</span>
                    <div className="sup-kpi-icon"><Users size={16} /></div>
                  </div>
                  <div className="sup-kpi-number">{totalMembers}</div>
                  <div className="sup-kpi-footer">
                    <span>Cardholders in Network</span>
                    <strong style={{ color: '#047857' }}>Active Base</strong>
                  </div>
                </div>

                {/* 2. Total Live Offers */}
                <div className="sup-kpi-card">
                  <div className="sup-card-top-stripe" />
                  <div className="sup-kpi-head">
                    <span className="sup-kpi-title">Published Deals</span>
                    <div className="sup-kpi-icon"><Tag size={16} color="#059669" /></div>
                  </div>
                  <div className="sup-kpi-number">{totalOffers}</div>
                  <div className="sup-kpi-footer">
                    <span>Active In Catalog</span>
                    <strong style={{ color: '#059669' }}>Live</strong>
                  </div>
                </div>

                {/* 3. Offer Engagements / Scans */}
                <div className="sup-kpi-card">
                  <div className="sup-card-top-stripe" />
                  <div className="sup-kpi-head">
                    <span className="sup-kpi-title">Offer Interactions</span>
                    <div className="sup-kpi-icon"><MousePointerClick size={16} color="#f59e0b" /></div>
                  </div>
                  <div className="sup-kpi-number" style={{ color: '#047857' }}>
                    {totalInteractions}
                  </div>
                  <div className="sup-kpi-footer">
                    <span>{promoCopies} Promos Copied</span>
                    <span style={{ color: '#f59e0b', fontWeight: 700 }}>{websiteClicks} Link Clicks</span>
                  </div>
                </div>

                {/* 4. Conversion Reach Ratio */}
                <div className="sup-kpi-card">
                  <div className="sup-card-top-stripe" />
                  <div className="sup-kpi-head">
                    <span className="sup-kpi-title">Member Reach Rate</span>
                    <div className="sup-kpi-icon"><TrendingUp size={16} color="#047857" /></div>
                  </div>
                  <div className="sup-kpi-number">{conversionRate}%</div>
                  <div className="sup-kpi-footer">
                    <span>Unique Cardholders</span>
                    <strong style={{ color: '#047857' }}>{uniqueVisitors} Members</strong>
                  </div>
                </div>
              </div>

              {/* Analytical Charts & Performance Deck */}
              <div className="sup-reports-grid">
                {/* Visual Ratio: Promo Copies vs Direct Link Clicks */}
                <div className="sup-panel-box">
                  <div className="sup-panel-head">
                    <h3>Engagement Breakdown</h3>
                    <span style={{ fontSize: '0.68rem', fontWeight: 800, color: '#047857', background: '#f0fdf4', padding: '3px 8px', borderRadius: '999px' }}>
                      Real-Time Logs
                    </span>
                  </div>

                  {(() => {
                    const totalAction = promoCopies + websiteClicks || 1;
                    const c = 289;
                    const promoDash = (promoCopies / totalAction) * c;
                    const linkDash = (websiteClicks / totalAction) * c;

                    return (
                      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-around', gap: '20px', padding: '10px 0' }}>
                        <div style={{ position: 'relative', width: 120, height: 120, minWidth: 120 }}>
                          <svg viewBox="0 0 110 110" style={{ transform: 'rotate(-90deg)' }} width="120" height="120">
                            <circle cx="55" cy="55" r="46" fill="transparent" stroke="#f1f5f9" strokeWidth="12" />
                            <circle
                              cx="55"
                              cy="55"
                              r="46"
                              fill="transparent"
                              stroke="#059669"
                              strokeWidth="12"
                              strokeDasharray={`${promoDash} ${c}`}
                              strokeDashoffset="0"
                            />
                            <circle
                              cx="55"
                              cy="55"
                              r="46"
                              fill="transparent"
                              stroke="#f59e0b"
                              strokeWidth="12"
                              strokeDasharray={`${linkDash} ${c}`}
                              strokeDashoffset={-promoDash}
                            />
                          </svg>
                          <div style={{ position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%, -50%)', textAlign: 'center' }}>
                            <div style={{ fontSize: '1.25rem', fontWeight: 800, color: '#064e3b', lineHeight: 1 }}>{totalInteractions}</div>
                            <div style={{ fontSize: '0.62rem', color: '#64748b', fontWeight: 700, textTransform: 'uppercase' }}>Actions</div>
                          </div>
                        </div>

                        <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', flex: 1 }}>
                          <div style={{ display: 'flex', justifyContent: 'space-between', padding: '6px 10px', background: '#f8fafc', borderRadius: '8px', fontSize: '0.74rem' }}>
                            <span><span style={{ display: 'inline-block', width: 8, height: 8, borderRadius: '50%', background: '#059669', marginRight: 6 }} /> Promo Codes Copied</span>
                            <strong style={{ color: '#064e3b' }}>{promoCopies}</strong>
                          </div>
                          <div style={{ display: 'flex', justifyContent: 'space-between', padding: '6px 10px', background: '#f8fafc', borderRadius: '8px', fontSize: '0.74rem' }}>
                            <span><span style={{ display: 'inline-block', width: 8, height: 8, borderRadius: '50%', background: '#f59e0b', marginRight: 6 }} /> Website Storefront Clicks</span>
                            <strong style={{ color: '#064e3b' }}>{websiteClicks}</strong>
                          </div>
                        </div>
                      </div>
                    );
                  })()}
                </div>

                {/* Offer-wise Interaction Leaderboard */}
                <div className="sup-panel-box">
                  <div className="sup-panel-head">
                    <h3>Top Performing Offerings</h3>
                    <span style={{ fontSize: '0.68rem', fontWeight: 800, color: '#b45309', background: '#fef3c7', padding: '3px 8px', borderRadius: '999px' }}>
                      Top 4 Deals
                    </span>
                  </div>

                  <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                    {stats?.offerBreakdown && stats.offerBreakdown.length > 0 ? (
                      stats.offerBreakdown.map((item, idx) => (
                        <div key={idx} style={{ background: '#f8fafc', border: '1px solid #f1f5f9', borderRadius: '8px', padding: '8px 12px' }}>
                          <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.74rem', fontWeight: 600, color: '#0f172a', marginBottom: '5px' }}>
                            <span style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', maxWidth: '240px' }}>{item.title}</span>
                            <strong>{item.interactions} Scans</strong>
                          </div>
                          <div style={{ height: '6px', background: '#e2e8f0', borderRadius: '999px', overflow: 'hidden' }}>
                            <div
                              style={{
                                height: '100%',
                                background: 'linear-gradient(90deg, #10b981 0%, #059669 100%)',
                                width: `${Math.min(100, (item.interactions / (totalInteractions || 1)) * 100)}%`
                              }}
                            />
                          </div>
                        </div>
                      ))
                    ) : (
                      <div style={{ fontSize: '0.78rem', color: '#94a3b8', padding: '16px' }}>
                        No offer interaction logs recorded yet.
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </>
          )}

          {/* ================= TAB 2: MEMBERS DIRECTORY ================= */}
          {activeTab === 'members' && (
            <div className="sup-panel-box">
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px', flexWrap: 'wrap', gap: '10px' }}>
                <div>
                  <h3 style={{ margin: 0, color: '#064e3b', fontSize: '1.15rem', fontFamily: 'Playfair Display, serif' }}>
                    Accredited Privilege Member Directory
                  </h3>
                  <span style={{ fontSize: '0.72rem', color: '#64748b' }}>
                    Cardholders eligible for your commercial partner privileges with real-time interest alerts
                  </span>
                </div>

                <div style={{ position: 'relative' }}>
                  <Search size={14} color="#94a3b8" style={{ position: 'absolute', top: 9, left: 10 }} />
                  <input
                    type="text"
                    placeholder="Search name, ID, 8-char code, district..."
                    value={memberSearch}
                    onChange={(e) => setMemberSearch(e.target.value)}
                    style={{ padding: '7px 12px 7px 30px', border: '1.5px solid #cbd5e1', borderRadius: '8px', fontSize: '0.78rem', minWidth: '240px', outline: 'none' }}
                  />
                </div>
              </div>

              <div style={{ overflowX: 'auto' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.8rem', textAlign: 'left' }}>
                  <thead>
                    <tr style={{ background: '#f0fdf4', color: '#064e3b', borderBottom: '1.5px solid #cbd5e1' }}>
                      <th style={{ padding: '10px 14px' }}>Member Name</th>
                      <th style={{ padding: '10px 14px' }}>Membership ID</th>
                      <th style={{ padding: '10px 14px' }}>Privilege Code</th>
                      <th style={{ padding: '10px 14px' }}>District & Club</th>
                      <th style={{ padding: '10px 14px' }}>Offer Interaction Alert</th>
                      <th style={{ padding: '10px 14px', textAlign: 'center' }}>Send Offer</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredMembers.map(m => (
                      <tr key={m.id} style={{ borderBottom: '1px solid #f1f5f9' }}>
                        <td style={{ padding: '12px 14px', fontWeight: 700, color: '#0f172a' }}>
                          {m.full_name}
                          <div style={{ fontSize: '0.7rem', color: '#64748b', fontWeight: 400 }}>{m.mobile}</div>
                        </td>

                        <td style={{ padding: '12px 14px', color: '#047857', fontWeight: 700 }}>
                          {m.membership_number}
                        </td>

                        <td style={{ padding: '12px 14px' }}>
                          <span style={{ fontFamily: 'monospace', fontWeight: 800, color: '#064e3b', background: '#f1f5f9', padding: '2px 6px', borderRadius: '5px' }}>
                            {m.privilege_code || 'PV9K4M2L'}
                          </span>
                        </td>

                        <td style={{ padding: '12px 14px', color: '#475569' }}>
                          District {m.district} • {m.club_name}
                        </td>

                        {/* COLUMN 1: VISITOR ALERT BADGE */}
                        <td style={{ padding: '12px 14px' }}>
                          {m.has_visited === 1 ? (
                            <span className="badge-alert-visited" title={`Interacted on ${m.last_interaction_at ? m.last_interaction_at.split('T')[0] : 'Recently'}`}>
                              <Eye size={12} />
                              <span>Interacted with Offers</span>
                            </span>
                          ) : (
                            <span className="badge-alert-idle">
                              <span>No Interaction Yet</span>
                            </span>
                          )}
                        </td>

                        {/* COLUMN 2: SEND OFFER DIRECT ACTION */}
                        <td style={{ padding: '12px 14px', textAlign: 'center' }}>
                          <button
                            className="btn-send-deal"
                            onClick={() => handleSendOfferEmail(m)}
                            disabled={sendingId === m.id}
                            title={`Dispatch exclusive discount proposal to ${m.email}`}
                          >
                            <Send size={12} />
                            <span>{sendingId === m.id ? 'Sending...' : 'Send Offer'}</span>
                          </button>
                        </td>
                      </tr>
                    ))}

                    {filteredMembers.length === 0 && (
                      <tr>
                        <td colSpan={6} style={{ textAlign: 'center', padding: '30px', color: '#94a3b8' }}>
                          No accredited members found matching query.
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* ================= TAB 3: MY COMMERCIAL OFFERS ================= */}
          {activeTab === 'offers' && (
            <SupplierOffersManager supplierId={currentSupplier.id} />
          )}
        </div>
      </div>
    </div>
  );
}